import type {AddSavePromise} from '@gaia/hooks/useIsSaved';
import fetchJSON from '@gaia/utils/fetchJSON';
import {setLocalStorageItem} from '@gaia/utils/localStorage';
import difference from 'lodash/difference';
import intersectionBy from 'lodash/intersectionBy';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';

import trackLayerAnalytics from './trackLayerAnalytics';

interface SaveLayers {
    userLayers: UserLayer[];
    savedUserLayers: UserLayer[] | undefined;
    erroredUserLayers: UserLayer[] | undefined;
    addSavePromise: AddSavePromise;
    isAuthenticated: boolean;
    customImports: string[] | undefined;
}

export default async function saveUserLayers({
    userLayers,
    savedUserLayers,
    erroredUserLayers,
    addSavePromise,
    isAuthenticated,
    customImports,
}: SaveLayers): Promise<UserLayer[]> {
    if (isEqual(userLayers, savedUserLayers)) return userLayers;
    if (isEqual(userLayers, erroredUserLayers)) return userLayers;

    trackLayerAnalytics(isAuthenticated, userLayers);

    setLocalStorageItem('userLayers', userLayers);

    if (!isAuthenticated) return userLayers;

    const layerTypes = userLayers.map((layer) => layer.layer.type);

    const savedLayerTypes = savedUserLayers?.map((layer) => layer.layer.type) ?? [];

    const addLayerTypes = difference(layerTypes, savedLayerTypes).filter(
        (layerType) => !customImports?.includes(layerType)
    );

    const removeLayerTypes = difference(savedLayerTypes, layerTypes);

    if (addLayerTypes.length === 0 && removeLayerTypes.length === 0) return userLayers;

    const savePromise = fetchJSON('/api/objects/mapSource/selection/', {
        method: 'POST',
        credentials: 'same-origin',
        body: JSON.stringify({
            add: addLayerTypes,
            remove: removeLayerTypes,
        }),
    });

    addSavePromise(savePromise);

    const response = await savePromise;

    const nextUserLayers: UserLayer[] = response.map(
        ({uniqueTilecacheKey}: {uniqueTilecacheKey: string}) => ({
            layer: {
                type: uniqueTilecacheKey,
                opacity: 1,
            },
            isVisible: false,
        })
    );

    return uniqBy(
        [...intersectionBy(userLayers, nextUserLayers, 'layer.type'), ...nextUserLayers],
        'layer.type'
    );
}
